/*
  useShapeStore - Dodano flipX, flipY, lockAspectRatio oraz wymiary.
*/
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { v4 as uuidv4 } from 'uuid';
import { getShapeById } from '../catalog/shapeCatalog';
import usePreferencesStore from './usePreferencesStore';

const useShapeStore = create(
  persist(
    (set, get) => ({
      shapeItems: {},
      selectedShapeId: null,
      isDraggingShape: false,
      dragStartPos: null,

      addShapeItem: (shapeId, position) => {
        const template = getShapeById(shapeId);
        if (!template) return;
        
        const preferences = usePreferencesStore.getState();
        const defaultStroke = preferences.objectStrokeColor;
        const defaultFill = preferences.objectFillColor;
        
        const id = uuidv4();
        const newItem = {
          id,
          shapeId: template.id,
          type: template.type,
          path: template.path,
          dimensions: { ...template.dimensions },
          x: position.x,
          y: position.y,
          rotation: 0,
          scale: 1,
          flipX: false,
          flipY: false,
          lockAspectRatio: true, // ✅ Domyślnie zablokowane proporcje
          customFill: defaultFill || template.render.fill,
          customStroke: defaultStroke || template.render.stroke,
          customStrokeWidth: template.render.strokeWidth
        };
        set(state => ({ shapeItems: { ...state.shapeItems, [id]: newItem }, selectedShapeId: id }));
        return id;
      },
      
      duplicateShapeItem: (id) => {
        const { shapeItems } = get();
        const item = shapeItems[id];
        if (!item) return;
        const newId = uuidv4();
        const newItem = { ...item, id: newId, x: item.x + 20, y: item.y + 20 };
        set((state) => ({ shapeItems: { ...state.shapeItems, [newId]: newItem }, selectedShapeId: newId }));
      },
      
      removeShapeItem: (id) => {
        set(state => {
          const newItems = { ...state.shapeItems };
          delete newItems[id];
          return { shapeItems: newItems, selectedShapeId: state.selectedShapeId === id ? null : state.selectedShapeId };
        });
      },
      
      updateShapeItem: (id, updates) => {
        set(state => {
          const item = state.shapeItems[id];
          if (!item) return state;

          let finalUpdates = { ...updates };

          // ✅ Obsługa skalowania proporcjonalnego dla kształtów
          if (item.lockAspectRatio && updates.dimensions) {
             const oldW = item.dimensions.width;
             const oldH = item.dimensions.height;
             const newW = updates.dimensions.width;
             const newH = updates.dimensions.height;

             if (newW !== oldW && newH === oldH) {
                const ratio = oldH / oldW;
                finalUpdates.dimensions.height = newW * ratio;
             } else if (newH !== oldH && newW === oldW) {
                const ratio = oldW / oldH;
                finalUpdates.dimensions.width = newH * ratio;
             }
          }

          return {
            shapeItems: {
              ...state.shapeItems,
              [id]: { ...item, ...finalUpdates }
            }
          };
        });
      },

      flipShapeItem: (id, axis) => {
        const item = get().shapeItems[id];
        if (!item) return;
        const updates = {};
        if (axis === 'horizontal') updates.flipX = !item.flipX;
        else if (axis === 'vertical') updates.flipY = !item.flipY;
        get().updateShapeItem(id, updates);
      },

      selectShapeItem: (id) => set({ selectedShapeId: id }),
      clearShapeSelection: () => set({ selectedShapeId: null }),
      startDraggingShape: (id, pos) => set({ isDraggingShape: true, selectedShapeId: id, dragStartPos: pos }),
      updateDraggingShape: (pos) => {
        const { isDraggingShape, selectedShapeId } = get();
        if (!isDraggingShape || !selectedShapeId) return;
        get().updateShapeItem(selectedShapeId, { x: pos.x, y: pos.y });
      },
      stopDraggingShape: () => set({ isDraggingShape: false, dragStartPos: null }),

      rotateShapeItem: (id, angle) => {
        const item = get().shapeItems[id];
        if(item) get().updateShapeItem(id, { rotation: item.rotation + angle });
      },
      
      scaleShapeItem: (id, factor) => {
        const item = get().shapeItems[id];
        if(item) get().updateShapeItem(id, { scale: Math.max(0.1, item.scale * factor) });
      }
    }),
    {
      name: 'floorcraft-shape-storage',
      partialize: (state) => ({ shapeItems: state.shapeItems }),
    }
  )
);

export default useShapeStore;